var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    BooleanBinding              = require("enyo/BooleanBinding"),
    Control                     = require("enyo/Control"),
    PerformanceMode             = require("enyo-perf/PerformanceMode"),
    PerformanceModeQueueSupport = require("enyo-perf/PerformanceModeQueueSupport"),
    ScrollerDecoratorClient     = require("enyo-ui/ScrollerDecoratorClient"),
    kind                        = require("enyo/kind"),
    throttle                    = require("enyo-perf/utils").throttle;

require("enyo-ui");

/**
 * [ScrollerDecorator]{@link enyo-ui.ScrollerDecorator} is a control that wraps
 * around a [Scroller]{@link enyo-ui.Scroller} and implements the infrastructure
 * needed to build UI elements that visualize or interact with the scroller.
 *
 * This control is not meant to be used directly, but rather to be extended to
 * build UI elements for scrollers, such as a scroll-bar.
 *
 * @ui
 * @class   enyo-ui.ScrollerDecorator
 * @extends enyo.Control
 * @mixes   enyo-perf.AbsoluteShowingQueueSupport
 * @mixes   enyo-perf.PerformanceModeQueueSupport
 * @abstract
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.ScrollerDecorator.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.ScrollerDecorator",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    mixins: [
        AbsoluteShowingQueueSupport,
        PerformanceModeQueueSupport
    ],

    /**
     * @private
     */
    classes: "enyo-ui-scroller-decorator",

    /**
     * Whether to expand the decorator to fit it's container.
     *
     * @type     {boolean}
     * @default  false
     * @public
     */
    fit: false,

    /**
     * The scroller we're currently working with.
     *
     * @type    {?enyo-ui.Scroller}
     * @default null
     * @private
     */
    scroller: null,

    /**
     * Flags whether the decorator us currently configuring it's chrome.
     *
     * @type {boolean}
     * @readonly
     * @public
     */
    configuringChrome: false,

    /**
     * A cached copy of the decorators' bounds.
     *
     * @readonly
     * @public
     */
    decoratorBounds: {

        /**
         * The width of the decorator
         *
         * @type {number}
         * @readonly
         * @public
         */
        width: 0,

        /**
         * The height of the decorator
         *
         * @type {number}
         * @readonly
         * @public
         */
        height: 0
    },

    /**
     * The chrome configuration that has currently been applied.
     *
     * @type {Object}
     * @readonly
     * @public
     */
    appliedChromeConfiguration: null,

    /**
     * Selector path used to find the nested
     * [Scroller]{@link enyo-ui.Scroller} control.
     *
     * @type {string}
     * @public
     */
    scrollerPath: ".$.scroller",

    /**
     * The debounce timeout in ms for [lazyConfigureChrome]{@link
     * enyo-ui.ScrollerDecorator#lazyConfigureChrome}.
     *
     * @type    {number}
     * @default 50
     * @public
     */
    lazyConfigureChromeTimeout: 50,

    /**
     * @private
     */
    bindings: [{
        kind: BooleanBinding,
        from: ".scroller.scrolling",
        to:   ".scrolling"
    }, {
        from: ".scroller.dimensions",
        to:   ".scrollerDimensions"
    }, {
        from: ".scroller.scrollLimits",
        to:   ".scrollLimits"
    }, {
        from: ".scroller.scrollPosition",
        to:   ".scrollPosition"
    }],

    /**
     * @private
     */
    handlers: {
        onScroll: "onScroll"
    },

    /**
     * Ensures the class instance has an ownProperty
     * `appliedChromeConfiguration` if none was passed to the constructor.
     *
     * @method
     * @private
     */
    importProps: kind.inherit(function(sup) {
        return function() {
            if (!this.hasOwnProperty("appliedChromeConfiguration")) {
                this.appliedChromeConfiguration = {};
            }
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createClient();
            this.createDecoration();
            this.controlParent = this.$.client;
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.lazyConfigureChrome = throttle(
                this.bindSafely(this.configureChrome),
                this.lazyConfigureChromeTimeout
            );
            sup.apply(this, arguments);
            this.scrollerPathChanged();
            this.fitChanged();
        };
    }),

    /**
     * Builds the client control.
     *
     * @private
     */
    createClient: function() {
        this.controlParent = null;
        this.createComponent({
            name:     "client",
            kind:     ScrollerDecoratorClient,
            classes:  "enyo-ui-scroller-decorator__client",
            isChrome: true
        });
    },

    /**
     * Build the controls that will decorate the scroller, such as scrollbars.
     * They will be created after the client control, making it easy to display
     * them on top of the client if needed.
     *
     * @abstract
     * @protected
     */
    createDecoration: function() {
        this.error("ScrollerDecorator must implement a `createDecoration` method.");
    },

    /**
     * @private
     */
    fitChanged: function() {
        var fit    = this.get("fit");

        if (fit) {
            !this.hasClass("enyo-fit") && this.addClass("enyo-fit");
        }
        else {
            while (this.hasClass("enyo-fit")) {
                this.removeClass("enyo-fit");
            }
        }
    },

    /**
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.updateDecoratorBoundsCache();
            this.configureChrome();
        };
    }),

    // ...........................
    // Scroller Setup

    /**
     * Update the referenced scroller if the path to the scroller has changed.
     *
     * @private
     */
    scrollerPathChanged: function() {
        var oldScroller = this.getScroller(),
            newScroller;

        newScroller = this.get(this.get("scrollerPath"));

        if (oldScroller !== newScroller) {
            this.disconnectScroller();
            newScroller && this.connectScroller(newScroller);
        }
    },

    /**
     * Connects a new scroller.
     *
     * @param {enyo-ui.Scroller}  scroller  The scroller
     * @private
     */
    connectScroller: function(scroller) {
        this.set("scroller", scroller);
        scroller.observe("destroyed", this.onScrollerDestroyed, this);
    },

    /**
     * Disconnects the current scroller.
     *
     * @private
     */
    disconnectScroller: function() {
        var scroller = this.getScroller();

        if (scroller) {
            scroller.unobserve("destroyed", this.onScrollerDestroyed);
        }
        this.set("scroller", null);
    },

    /**
     * Disconnect scroller if it gets destroyed
     *
     * @private
     */
    onScrollerDestroyed: function() {
        this.disconnectScroller();
    },

    /**
     * Gets the scroller control.
     *
     * @return {?enyo-ui.Scroller}  The scroller
     * @public
     */
    getScroller: function() {
        return this.scroller || null;
    },

    // ...........................
    // Bounds Cache

    /**
     * Gets cached decorator bounds.
     *
     * @return {Object}  The decorator bounds
     * @public
     */
    getDecoratorBounds: function() {
        return this.decoratorBounds || this.updateDecoratorBoundsCache();
    },

    /**
     * Updates the cached decorator bounds.
     *
     * @return {Object}  The decorator bounds
     * @public
     */
    updateDecoratorBoundsCache: function() {
        this.decoratorBounds = this.getBounds();

        return this.decoratorBounds;
    },

    // ...........................
    // Chrome Configuration & Synchronization

    /**
     * Respond to changed [scrollerDimensions]{@link
     * enyo-ui.Scroller#dimensions} by updating chrome configuration if content
     * or scroller changed size.
     *
     * @param {Object}  oldDimensions  The old dimensions
     * @param {Object}  newDimensions  The new dimensions
     * @protected
     */
    scrollerDimensionsChanged: function(oldDimensions, newDimensions) {
        if (!oldDimensions
            || oldDimensions.scrollerWidth !== newDimensions.scrollerWidth
            || oldDimensions.scrollerHeight !== newDimensions.scrollerHeight
            || oldDimensions.contentWidth !== newDimensions.contentWidth
            || oldDimensions.contentHeight !== newDimensions.contentHeight
        ) {
            this.configureChrome();
        }
    },

    /**
     * Figures out the best chrome configuration for the current state of the
     * scroller and applies it.
     *
     * This is done by calculating the ideal configuration for the current
     * [scrollerDimensions]{@link enyo-ui.Scroller#dimensions}, applying it and
     * verifying if the newly applied configuration is still ideal.
     *
     * Depending on the Decorator implementation, the newly applied chrome
     * configuration may alter the dimensions of the contents of the decorator.
     * This in turn may cause the newly applied configuration to no longer be
     * ideal. If this is the case we compute a new ideal configuration and apply
     * and verify it again.
     *
     * This cycle continues until either an appropriate configuration is found
     * or the same configuration is calculated twice, indicating a catch 22
     * scenario.
     *
     * If a catch 22 is encountered a set of all tried configurations is passed
     * to [solveChromeConflict]{@link
     * enyo-ui.ScrollerDecorator#solveChromeConflict}, which should return the
     * most appropriate configuration. This configuration is then applied,
     * ending the configuration cycle.
     *
     * @public
     */
    configureChrome: function() {
        var scroller = this.getScroller(),
            contentResized,
            triedConfigurations,
            appliedConfiguration,
            calculatedConfiguration;

        // Cancel throttled call
        this.lazyConfigureChrome.cancel();

        // Applying new chrome settings might resize the scroller, which in turn
        // might trigger this method.
        if (this.configuringChrome) {
            return;
        }

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("configureChrome");

            return;
        }
        this.removeFromAbsoluteShowingQueue("configureChrome");

        // Defer when in performanceMode
        if (PerformanceMode.on) {
            this.addToPerformanceModeQueue("configureChrome");

            // If the scroller is scrolling the chrome will be synchronized
            // automatically. If it isn't, we can synchronize it safely without
            // impacting framerate.
            scroller && !scroller.scrolling && this.syncChrome();

            return;
        }
        this.removeFromPerformanceModeQueue("configureChrome");

        // Start configuring
        this.set("configuringChrome", true);
        appliedConfiguration    = this.appliedChromeConfiguration;
        calculatedConfiguration = this.calcChromeConfiguration();
        triedConfigurations     = [];

        // The applied chrome configuration is not ideal for the current
        // scroller dimensions. Apply the newly calculated chrome configuration
        // and re-evaluate.
        while (!this.isIdenticalChromeConfiguration(appliedConfiguration, calculatedConfiguration)) {
            triedConfigurations.push(appliedConfiguration);

            // This chrome configuration has not been tried yet.
            if (this.isNewChromeConfiguration(calculatedConfiguration, triedConfigurations)) {

                // Apply the new setting
                contentResized       = this.applyChromeConfiguration(calculatedConfiguration);
                appliedConfiguration = calculatedConfiguration;
                this.set("appliedChromeConfiguration", appliedConfiguration);

                if (contentResized) {
                    // Calculate ideal configuration for the new scrollerDimensions
                    calculatedConfiguration = this.calcChromeConfiguration();
                }
                else {
                    break;
                }
            }

            // We have tried this chrome configuration already. This probably
            // means we cannot find a perfect configuration. Lets try to figure
            // out which of the configurations we tried is the most appropriate.
            else {
                // Choose the most appropriate setting
                calculatedConfiguration = this.solveChromeConflict(triedConfigurations);

                // Apply the new setting
                contentResized       = this.applyChromeConfiguration(calculatedConfiguration);
                appliedConfiguration = calculatedConfiguration;
                this.set("appliedChromeConfiguration", appliedConfiguration);

                break;
            }
        }

        // Make sure our newly-configured chrome is synced up with scrollBounds
        this.syncChrome();

        // Done configuring
        this.set("configuringChrome", false);
    },

    /**
     * A debounced version of [configureChrome]{@link
     * enyo-ui.ScrollerDecorator#configureChrome}. You can control the debounce
     * timeout by setting [lazyConfigureChromeTimeout]{@link
     * enyo-ui.ScrollerDecorator#lazyConfigureChromeTimeout}
     *
     * @method   lazyConfigureChrome
     * @memberof enyo-ui.ScrollerDecorator
     * @instance
     * @public
     */

    /**
     * Determines if the supplied chrome configurations are identical by means
     * of a strict equality check on all ownProperties of both chrome
     * configurations. Override this method if this is insufficient.
     *
     * @param  {Object}   a  First chrome configuration
     * @param  {Object}   b  Second chrome configuration
     * @return {boolean}  `true` if identical chrome configuration, `false`
     *                    otherwise.
     * @protected
     */
    isIdenticalChromeConfiguration: function(a, b) {
        var key;

        for (key in a) {
            if (a.hasOwnProperty(key) && a[key] !== b[key]) {
                return false;
            }
        }

        for (key in b) {
            if (b.hasOwnProperty(key) && b[key] !== a[key]) {
                return false;
            }
        }

        return true;
    },

    /**
     * Determines whether a candidate chrome configuration is already in the
     * list of chrome configurations which were already tried during the current
     * chrome configuration cycle.
     *
     * @param  {Object}   configuration        The possibly new chrome
     *                                         configuration
     * @param  {Array}    triedConfigurations  The tried configurations
     * @return {boolean}  `true` if the configuration is new, `false` otherwise.
     * @private
     */
    isNewChromeConfiguration: function(configuration, triedConfigurations) {
        var i = 0,
            n = triedConfigurations.length,
            known;

        for (i, n; i < n; i++) {
            known = triedConfigurations[i];

            if (this.isIdenticalChromeConfiguration(configuration, known)) {
                return false;
            }
        }

        return true;
    },

    /**
     * Calculate a chrome configuration that seems appropriate for the current
     * [scrollerDimensions]{@link enyo-ui.ScrollerDecorator#scrollerDimensions}.
     *
     * Description of the configuration should be a flat object with key-value
     * pairs compatible with strict equality checks. For instance:
     *
     * ```
     * {
     *     horizontalScrollbar: false,
     *     verticalScrollbar: true
     * }
     * ```
     *
     * @return {Object|Error}  The chrome configuration
     * @abstract
     * @protected
     */
    calcChromeConfiguration: function() {
        return this.error("ScrollerDecorator must implement a `calcChromeConfiguration` method.");
    },

    /**
     * Apply the chrome configuration. Don't forget to trigger a resize on the
     * client if applying a new configuration changes the clients' dimensions.
     *
     * @param  {Object}   configuration  The chrome configuration to apply
     * @return {Boolean}  Whether the new chrome configuration resized the
     *                    client.
     * @abstract
     * @protected
     */
    applyChromeConfiguration: function(configuration) {
        this.error("ScrollerDecorator must implement an `applyChromeConfiguration` method.", configuration);

        return false;
    },

    /**
     * Choose the most appropriate chrome configuration from the supplied list.
     *
     * @param  {Array}         configurations  The configurations to choose from
     * @return {Object|Error}  The chosen configuration
     * @abstract
     * @protected
     */
    solveChromeConflict: function(configurations) {
        return this.error("ScrollerDecorator must implement a `solveChromeConflict` method.", configurations);
    },

    /**
     * Synchronize the state of the current chrome with the scroller. When the
     * scroller is scrolling, this method will be executed on every frame, so
     * try not to impact the framerate.
     *
     * @abstract
     * @protected
     */
    syncChrome: function() {
        this.error("ScrollerDecorator must implement a `syncChrome` method.");
    },

    // ...........................
    // Scroller Event Handlers

    /**
     * If our scroller is scrolling, call a dedicated high-performance chrome
     * synchronization method.
     *
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object
     * @private
     */
    onScroll: function(sender, event) {
        if (event.originator === this.getScroller()) {
            this.syncChrome();
        }
    },

    // ...........................
    // Destruction

    /**
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.disconnectScroller();
            sup.apply(this, arguments);
        };
    })
});
